// Considered: AccordionAtom — rejected because it lacks the drag-grip, numbered badge, and per-segment actions required by the segment card IA. See spec Reuse Audit.
// 2026-07 drill-in redesign (SC4): this molecule now renders compact summary
// rows only — clicking a row bubbles `onEditSegment` so the host (the level
// Inspector) opens `SegmentPanelMolecule` as a rail overlay. The former inline
// accordion body (label input, filter editor, sort override, remove) moved
// there; editing a segment no longer pushes sibling rows down.
import React from 'react';
import { useTranslation } from 'localization';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import type { ContextFlowSegment } from '../../../types/contextFlow';
import './ContextSegmentInspectorMolecule.scss';

export interface ContextSegmentInspectorMoleculeProps {
  segments: ContextFlowSegment[];
  levelColor?: string;
  onAddSegment: () => void;
  /** Opens the segment's drill-in overlay (`SegmentPanelMolecule`) in the host rail. */
  onEditSegment: (id: string) => void;
  readOnly?: boolean;
}

const ContextSegmentInspectorMolecule: React.FC<ContextSegmentInspectorMoleculeProps> = ({
  segments,
  levelColor = 'var(--color-primary)',
  onAddSegment,
  onEditSegment,
  readOnly = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="context-segment-inspector-molecule">
      {/* Section header */}
      <div className="context-segment-inspector-molecule__header">
        <span className="context-segment-inspector-molecule__eyebrow">
          {t('context.editor.inspector.segments.title')}
        </span>
        {segments.length > 0 && (
          <ChipAtom
            label={t('context.editor.inspector.segments.bucketsCount', { n: segments.length })}
            className="context-segment-inspector-molecule__count-chip"
          />
        )}
        <span className="context-segment-inspector-molecule__spacer" />
        {!readOnly && (
          <button
            type="button"
            className="context-segment-inspector-molecule__add-btn"
            data-testid="csi-add-segment-btn"
            onClick={onAddSegment}
          >
            <i className="pi pi-plus" aria-hidden="true" />
            {t('context.editor.inspector.segments.add')}
          </button>
        )}
      </div>

      <p className="context-segment-inspector-molecule__desc">
        {t('context.editor.inspector.segments.description')}
      </p>

      {/* Segment summary rows */}
      {segments.length > 0 && (
        <div className="context-segment-inspector-molecule__list">
          {segments.map((seg, i) => {
            const filterCount = seg.filters?.propertyFilters?.length ?? 0;

            return (
              <button
                key={seg.id}
                type="button"
                className="context-segment-inspector-molecule__row"
                data-testid={`csi-seg-row-${seg.id}`}
                onClick={() => onEditSegment(seg.id)}
              >
                <i className="pi pi-bars context-segment-inspector-molecule__grip" aria-hidden="true" />
                <span
                  className="context-segment-inspector-molecule__num-badge"
                  style={{ background: levelColor }}
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <div className="context-segment-inspector-molecule__row-info">
                  <span className="context-segment-inspector-molecule__seg-label">{seg.label.default}</span>
                  {filterCount === 0 && (
                    <span className="context-segment-inspector-molecule__no-filter-hint">
                      {t('context.editor.inspector.segments.noFiltersYet')}
                    </span>
                  )}
                </div>
                <span className="context-segment-inspector-molecule__filter-count">{filterCount}</span>
                <i className="pi pi-chevron-right context-segment-inspector-molecule__chevron" aria-hidden="true" />
              </button>
            );
          })}

          <p className="context-segment-inspector-molecule__hint">
            <i className="pi pi-info-circle" aria-hidden="true" />
            {t('context.editor.inspector.segments.hint')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ContextSegmentInspectorMolecule;
